import React, { useEffect, useMemo, useState } from "react"
import { Sparkles, ChevronDown, ChevronRight, Plus, X } from "lucide-react"

import { funEU } from "../../../../utils/firebase"
import Button from "../../../../components/ui/button/button"
import Input from "../../../../components/ui/inputs/input"
import Textarea from "../../../../components/ui/inputs/textarea"
import Select from "../../../../components/ui/select/select"

import { AI_TOOLS, AI_MODELS, PLACEHOLDER_VARIABLES, localStorageKey } from "./registry"

/**
 * Embedded AI tooling panel for the email drawer.
 *
 * The panel reads the tool definition from `registry.js` for sensible defaults,
 * but every field shown in the UI is editable so devs can tweak prompts,
 * models, schemas, and tool-specific variables on the fly. Edits persist to
 * localStorage keyed by tool id + field, so a refresh doesn't lose work.
 *
 * On Run, the panel calls the `runEmailAiTool` callable in the
 * `functions-communications` codebase, which is a thin OpenAI proxy: it
 * substitutes `{{placeholders}}` server-side, calls OpenAI, and returns the
 * result. The panel renders structured (parsed JSON) results in a friendly
 * card, and falls back to a raw text view if the response wasn't JSON.
 */
function AIToolsPanel({ comm }) {
	const [open, setOpen] = useState(false)

	// Selected tool — hard-coded to "classifier" for v1 since it's the only
	// registered tool. When more tools land, swap this for a dropdown.
	const [selectedToolId] = useState("classifier")
	const tool = AI_TOOLS[selectedToolId]

	// Editable state, seeded from the tool's defaults and persisted to localStorage.
	const [model, setModel] = useState(() =>
		readLocalStorage(localStorageKey(tool.id, "model"), tool.default_model)
	)
	const [temperature, setTemperature] = useState(() =>
		String(readLocalStorage(localStorageKey(tool.id, "temperature"), tool.default_temperature))
	)
	const [systemInstructions, setSystemInstructions] = useState(() =>
		readLocalStorage(localStorageKey(tool.id, "system"), tool.default_system_instructions)
	)
	const [userInstructions, setUserInstructions] = useState(() =>
		readLocalStorage(localStorageKey(tool.id, "user"), tool.default_user_instructions)
	)
	const [customInputs, setCustomInputs] = useState(() => {
		const next = {}
		for (const input of tool.custom_inputs || []) {
			// Non-editable inputs (e.g. the classifier's fixed taxonomy) always
			// take their value from the registry. Reading from localStorage here
			// would mean stale lists persisting across deploys, which is exactly
			// the trap we hit before. Editable inputs still cache so user tweaks
			// to prompts and other custom values survive a refresh.
			if (input.editable === false) {
				next[input.key] = input.default
				continue
			}
			next[input.key] = readLocalStorage(
				localStorageKey(tool.id, `custom:${input.key}`),
				input.default
			)
		}
		return next
	})

	// Persist edits to localStorage on change.
	useEffect(() => writeLocalStorage(localStorageKey(tool.id, "model"), model), [tool.id, model])
	useEffect(() => writeLocalStorage(localStorageKey(tool.id, "temperature"), temperature), [
		tool.id,
		temperature,
	])
	useEffect(() => writeLocalStorage(localStorageKey(tool.id, "system"), systemInstructions), [
		tool.id,
		systemInstructions,
	])
	useEffect(() => writeLocalStorage(localStorageKey(tool.id, "user"), userInstructions), [
		tool.id,
		userInstructions,
	])
	useEffect(() => {
		// Skip persistence for non-editable inputs; they always come from the
		// registry on next load anyway, so caching them just creates drift.
		const nonEditableKeys = new Set(
			(tool.custom_inputs || []).filter((i) => i.editable === false).map((i) => i.key)
		)
		for (const [key, value] of Object.entries(customInputs)) {
			if (nonEditableKeys.has(key)) continue
			writeLocalStorage(localStorageKey(tool.id, `custom:${key}`), value)
		}
	}, [tool.id, customInputs])

	// Run state.
	const [running, setRunning] = useState(false)
	const [error, setError] = useState(null)
	const [result, setResult] = useState(null)

	const modelOptions = useMemo(
		() => Object.fromEntries(AI_MODELS.map((m) => [m, m])),
		[]
	)

	const resetToDefaults = () => {
		setModel(tool.default_model)
		setTemperature(String(tool.default_temperature))
		setSystemInstructions(tool.default_system_instructions)
		setUserInstructions(tool.default_user_instructions)
		const next = {}
		for (const input of tool.custom_inputs || []) {
			next[input.key] = input.default
		}
		setCustomInputs(next)
	}

	const run = async () => {
		setRunning(true)
		setError(null)
		setResult(null)

		// Convert custom inputs to plain string variables. List-typed inputs are
		// joined with ", " — that's the convention the default classifier prompt
		// relies on, and most other "list of strings" use cases want the same.
		const customVariables = {}
		for (const input of tool.custom_inputs || []) {
			const value = customInputs[input.key]
			if (input.type === "list" && Array.isArray(value)) {
				customVariables[input.key] = value.join(", ")
			} else {
				customVariables[input.key] = String(value ?? "")
			}
		}

		try {
			const callable = funEU.httpsCallable("runEmailAiTool")
			const response = await callable({
				comm_id: comm.id,
				model,
				temperature: Number(temperature) || 0,
				system_instructions: systemInstructions,
				user_instructions: userInstructions,
				response_format: tool.response_format,
				json_schema: tool.json_schema,
				json_schema_name: tool.json_schema_name,
				json_schema_strict: tool.json_schema_strict,
				custom_variables: customVariables,
				// Tools can opt in to multimodal — when true, the backend extracts
				// non-tracking-pixel images from the body and attaches them as
				// `input_image` content blocks alongside the text prompt.
				pass_images: tool.pass_images === true,
				pass_external_images: tool.pass_external_images === true,
				include_inline_images: tool.include_inline_images === true,
				include_attachment_images: tool.include_attachment_images === true,
				include_file_attachments: tool.include_file_attachments === true,
				// Tools can also opt in to having the structured result persisted
				// back to the communication doc. Used by the classifier so its
				// output drives the email row's Classification + Confidence columns.
				save_classification: tool.save_to_communication === true,
			})
			setResult(response.data || null)
		} catch (err) {
			setError(err?.message || String(err))
		} finally {
			setRunning(false)
		}
	}

	return (
		<div className={`ai-panel ${open ? "is-open" : ""}`}>
			<button
				type="button"
				className="ai-panel-toggle"
				onClick={() => setOpen((o) => !o)}>
				<span className="ai-panel-toggle-icon">
					{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
				</span>
				<Sparkles size={14} />
				<span>AI tooling — {tool.name}</span>
			</button>

			{open && (
				<div className="ai-panel-body">
					<p className="ai-panel-description">{tool.description}</p>

					<div className="ai-panel-grid">
						<div className="ai-panel-field">
							<Select
								showLabel
								placeholder="Model:"
								value={model}
								selected={model}
								onSelect={(option) => setModel(option.option)}
								options={modelOptions}
							/>
						</div>

						<div className="ai-panel-field">
							<Input
								type="text"
								placeholder="Temperature (0–2):"
								value={temperature}
								onChange={setTemperature}
								showLabel
							/>
						</div>
					</div>

					<div className="ai-panel-field">
						<Textarea
							placeholder="System instructions"
							value={systemInstructions}
							onChange={setSystemInstructions}
							showLabel
							rows={5}
						/>
					</div>

					<div className="ai-panel-field">
						<Textarea
							placeholder="User instructions"
							value={userInstructions}
							onChange={setUserInstructions}
							showLabel
							rows={9}
						/>
						<small className="ai-panel-helper">
							Available placeholders:{" "}
							{PLACEHOLDER_VARIABLES.map((v) => (
								<code key={v.key}>{`{{${v.key}}}`}</code>
							))}
							{tool.custom_inputs?.length > 0 && (
								<>
									{" "}
									Custom:{" "}
									{tool.custom_inputs.map((input) => (
										<code key={input.key}>{`{{${input.key}}}`}</code>
									))}
								</>
							)}
						</small>
					</div>

					{(tool.custom_inputs || []).map((input) => (
						<CustomInputField
							key={input.key}
							input={input}
							value={customInputs[input.key]}
							onChange={(next) =>
								setCustomInputs((prev) => ({ ...prev, [input.key]: next }))
							}
						/>
					))}

					<div className="ai-panel-actions">
						<Button
							small={true}
							label="Run"
							icon={<Sparkles size={14} />}
							loading={running}
							loadingText="Running..."
							onClick={() => run()}
							disabled={!userInstructions || running}
						/>
						<Button
							className="OUTLINE"
							small={true}
							label="Reset to defaults"
							onClick={() => resetToDefaults()}
						/>
					</div>

					{error && (
						<div
							className="ai-panel-error"
							role="alert">
							{error}
						</div>
					)}

					{result && <ResultDisplay result={result} />}
				</div>
			)}
		</div>
	)
}

/**
 * Renders a single custom input from a tool's definition. Supported types:
 *
 *   - `list` with `editable: true`  → array of inputs with add/remove buttons.
 *   - `list` with `editable: false` → read-only badge display showing every
 *     item in the fixed list (used for tools where the taxonomy must stay
 *     stable, e.g. the email classifier).
 *   - anything else                 → plain text input.
 */
function CustomInputField({ input, value, onChange }) {
	if (input.type === "list") {
		const list = Array.isArray(value) ? value : []
		const editable = input.editable !== false

		if (!editable) {
			return (
				<div className="ai-panel-field">
					<label className="ai-panel-field-label">{input.label}</label>
					<div className="ai-panel-list-readonly">
						{list.map((item, index) => (
							<span
								key={index}
								className="ai-panel-list-readonly-item">
								{item}
							</span>
						))}
					</div>
					{input.help_text && (
						<small className="ai-panel-helper">{input.help_text}</small>
					)}
				</div>
			)
		}

		return (
			<div className="ai-panel-field">
				<label className="ai-panel-field-label">{input.label}</label>
				<div className="ai-panel-list">
					{list.map((item, index) => (
						<div
							key={index}
							className="ai-panel-list-row">
							<Input
								type="text"
								placeholder={input.placeholder || ""}
								value={item}
								onChange={(next) => {
									const copy = [...list]
									copy[index] = next
									onChange(copy)
								}}
							/>
							<button
								type="button"
								className="ai-panel-list-remove"
								onClick={() => onChange(list.filter((_, i) => i !== index))}
								aria-label="Remove item">
								<X size={14} />
							</button>
						</div>
					))}
					<button
						type="button"
						className="ai-panel-list-add"
						onClick={() => onChange([...list, ""])}>
						<Plus size={14} /> Add
					</button>
				</div>
				{input.help_text && (
					<small className="ai-panel-helper">{input.help_text}</small>
				)}
			</div>
		)
	}

	return (
		<div className="ai-panel-field">
			<Input
				type="text"
				placeholder={input.label}
				value={value ?? ""}
				onChange={onChange}
				showLabel
			/>
			{input.help_text && <small className="ai-panel-helper">{input.help_text}</small>}
		</div>
	)
}

function ResultDisplay({ result }) {
	const {
		output_text,
		parsed,
		model_used,
		usage,
		images_attached,
		external_images_attached,
		inline_images_attached,
		attachment_images_attached,
		files_attached,
		saved_to_communication,
	} = result || {}

	return (
		<div className="ai-panel-result">
			<div className="ai-panel-result-meta">
				<small>
					<strong>Model:</strong> {model_used || "—"}
				</small>
				{usage && (
					<small>
						<strong>Tokens:</strong> {usage.input_tokens || 0} in /{" "}
						{usage.output_tokens || 0} out
					</small>
				)}
				{typeof images_attached === "number" && images_attached > 0 && (
					<small>
						<strong>Images:</strong> {images_attached} attached
					</small>
				)}
				{typeof external_images_attached === "number" && external_images_attached > 0 && (
					<small>
						<strong>External images:</strong> {external_images_attached}
					</small>
				)}
				{typeof inline_images_attached === "number" && inline_images_attached > 0 && (
					<small>
						<strong>Inline images:</strong> {inline_images_attached}
					</small>
				)}
				{typeof attachment_images_attached === "number" && attachment_images_attached > 0 && (
					<small>
						<strong>Attachment images:</strong> {attachment_images_attached}
					</small>
				)}
				{typeof files_attached === "number" && files_attached > 0 && (
					<small>
						<strong>Files:</strong> {files_attached} attached
					</small>
				)}
				{saved_to_communication !== undefined && (
					<small>
						<strong>Saved to row:</strong> {saved_to_communication ? "yes" : "no"}
					</small>
				)}
			</div>

			{parsed ? (
				<pre className="ai-panel-result-json">{JSON.stringify(parsed, null, 2)}</pre>
			) : (
				<pre className="ai-panel-result-text">{output_text || "(empty response)"}</pre>
			)}
		</div>
	)
}

const readLocalStorage = (key, fallback) => {
	try {
		const raw = window.localStorage.getItem(key)
		if (raw === null) return fallback
		return JSON.parse(raw)
	} catch {
		return fallback
	}
}

const writeLocalStorage = (key, value) => {
	try {
		window.localStorage.setItem(key, JSON.stringify(value))
	} catch {
		// Quota exceeded or storage disabled — silent failure is fine, the in-memory
		// state still works for the current session.
	}
}

export default AIToolsPanel
